import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';
import TanStackTable from '../../../../../../../../shared/design-system/ui/tanstack-table';
import { PaginationOptionsChangePayload } from '../../../../../../../../shared/design-system/ui/tanstack-table/types';
import {
  getPhoneNumberColumnDefinitions,
} from './column-definitions';

import { PhoneNumber, PhoneNumberListResponse } from '../../../../../../types/phone-numbers';

interface PhoneNumberListTableProps {
  data?: PhoneNumberListResponse;
  paginationOptions?: {
    page: number;
    limit: number;
    totalElements: number;
  };
  onPaginationOptionsChange?: (options: any) => void;
  isLoading?: boolean;
  selectedRowIds?: (string | number)[];
  onRowSelect?: (rowId: string | number, isSelect: boolean) => void;
  onRowSelectAll?: (rowIds: (string | number)[], isSelect: boolean) => void;
  onClearSelection?: () => void;
  isSelectAllAcrossPages?: boolean;
  onSelectAllAcrossPages?: () => void;
  selectAllDisplayCount?: number | null;
}

const PhoneNumberListTable: React.FC<PhoneNumberListTableProps> = ({
  data,
  paginationOptions,
  onPaginationOptionsChange,
  isLoading = false,
  selectedRowIds = [],
  onRowSelect,
  onRowSelectAll,
  onClearSelection,
  isSelectAllAcrossPages = false,
  onSelectAllAcrossPages,
  selectAllDisplayCount = null,
}) => {
  // Column definitions
  const columns = useMemo(() => getPhoneNumberColumnDefinitions(), []);

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (payload: PaginationOptionsChangePayload) => {
      if (onPaginationOptionsChange) {
        onPaginationOptionsChange({
          page: payload.page || paginationOptions?.page || 1,
          limit: payload.limit || paginationOptions?.limit || 100,
          totalElements:
            paginationOptions?.totalElements || data?.total || 0,
        });
      }
    },
    [onPaginationOptionsChange, paginationOptions, data],
  );

  // Use dummy data if no data provided
  const tableData: PhoneNumber[] = useMemo(
    () => data?.phoneNumbers || [] as PhoneNumber[],
    [data?.phoneNumbers],
  );

  // Calculate total rows - prioritize API data, fallback to tableData length
  const totalRows = useMemo(() => {
    if (paginationOptions?.totalElements) {
      return paginationOptions.totalElements;
    }
    if (data?.total) {
      return data.total;
    }
    // Fallback to actual data length if no API total provided
    return tableData.length;
  }, [paginationOptions?.totalElements, data?.total, tableData.length]);

  const showPagination = totalRows > 25;

  // Memoize paginationOptions
  const paginationOptionsForTable = useMemo(
    () =>
      showPagination
        ? {
            page: paginationOptions?.page || 1,
            limit: paginationOptions?.limit || 25,
            totalRows,
            onPaginationOptionsChange: handlePaginationChange,
          }
        : undefined,
    [showPagination, paginationOptions, totalRows, handlePaginationChange],
  );

  // Memoize paginationSizeOptions
  const paginationSizeOptions = useMemo(() => [25, 50, 100], []);

  // Default pinned columns - phone number column should be pinned on left
  const defaultPinnedColumns = useMemo(() => ['phoneNumber'], []);

  // Pinned columns state
  const [pinnedColumns] = React.useState<string[]>(['phoneNumber']);
  const [rightPinnedColumns] = React.useState<string[]>(['actions']);

  // Handle row selection - wrapper to handle ID conversion
  const handleRowSelect = useCallback(
    (rowId: string | number, isSelect: boolean) => {
      if (onRowSelect) {
        onRowSelect(rowId, isSelect);
      }
    },
    [onRowSelect],
  );

  // Handle select all on current page
  const handleRowSelectAll = useCallback(
    (rowIds: (string | number)[], isSelect: boolean) => {
      if (onRowSelectAll) {
        onRowSelectAll(rowIds, isSelect);
      }
    },
    [onRowSelectAll],
  );

  // Handle select all across pages
  const handleSelectAllAcrossPages = useCallback(() => {
    if (onSelectAllAcrossPages) {
      onSelectAllAcrossPages();
    }
  }, [onSelectAllAcrossPages]);

  const tableClass = classNames('phone-number-list-table', {
    'phone-number-list-table--no-pagination': !showPagination,
  });

  return (
    <div className={tableClass}>
      <TanStackTable
        columns={columns}
        rows={tableData}
        isLoading={isLoading}
        paginationOptions={paginationOptionsForTable}
        paginationSizeOptions={paginationSizeOptions}
        pinnedColumns={pinnedColumns}
        rightPinnedColumns={rightPinnedColumns}
        defaultPinnedColumns={defaultPinnedColumns}
        enableColumnResizing={false}
        enableSorting={false}
        enableRowSelection={true}
        selectedRowIds={selectedRowIds}
        onRowSelect={handleRowSelect}
        onRowSelectAll={handleRowSelectAll}
        onClearSelection={onClearSelection}
        isSelectAllAcrossPages={isSelectAllAcrossPages}
        onSelectAllAcrossPages={handleSelectAllAcrossPages}
        allowIndivualRowDeSelectionAcrossAllPage={true}
        selectAllDisplayCount={selectAllDisplayCount}
        isRowDisabled={() => false}
        maxSelectedRowsLimit={null}
        itemName="Number"
      />
    </div>
  );
};

export default PhoneNumberListTable;
